// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { ReduxMiddleware, ReduxStore, ReduxNext } from '@makemydeal/dr-shared-store';
import { featureToggleSelectors, initActionTypes } from '@makemydeal/dr-shared-store';

// selectors
import { getCreditDecisionLender, getTitlingLenderWithAddress } from '../selectors/offerRedux';

// actions
import { fetchLenderAddress, applyLenderResponse, cacheLender } from '../actions/lenderActionCreators';
import { FETCH_LENDER_ADDRESS_SUCCESS } from '../actionTypes/lenderAddressActionTypes';

// types
import type { LenderAddressResponse, StateTree } from '@makemydeal/dr-dash-types';

const { INIT_SUCCESS } = initActionTypes;

/**
 * Guarded per-scenario fetch, reading from and dispatching against the scope this middleware is currently running
 * in. On a multi-scenario deal a scoped INIT_SUCCESS fires for every scenario (offerMiddleware turns each
 * scenario's scoped INIT_PENCIL_SUCCESS into a scoped INIT_SUCCESS), so this runs once per scenario against that
 * scenario's own state; the scoped `store.dispatch` tags the RSAA with its scope, routing the response back into
 * the same scenario.
 */
const dispatchLenderAddressFetch = (store: ReduxStore<StateTree>, state: StateTree): void => {
    if (!featureToggleSelectors.enableLenderAddress(state)) return;

    const lenderId = getCreditDecisionLender(state)?.id;
    if (!lenderId) return;

    const dealTitlingLender = getTitlingLenderWithAddress(state);
    // If the deal already carries titling address data, cache it (titling + credit-decision) and skip the fetch
    // entirely, so switching away from this lender and back within the session restores both without a re-fetch.
    if (Object.values(dealTitlingLender || {}).filter(Boolean).length > 0) {
        store.dispatch(cacheLender(lenderId));
        return;
    }

    store.dispatch(
        featureToggleSelectors.isLoadEnrichmentRebaselineEnabled(state)
            ? fetchLenderAddress(lenderId, { rebaselineUnsavedChangesWhenClean: true })
            : fetchLenderAddress(lenderId)
    );
};

export const middleware: ReduxMiddleware<StateTree> = (store: ReduxStore<StateTree>) => (next: ReduxNext) => (action: AnyFSA) => {
    next(action);
    switch (action.type) {
        case INIT_SUCCESS: {
            // Fires once per scenario (scoped). Fetch for whichever scope this INIT_SUCCESS belongs to.
            dispatchLenderAddressFetch(store, store.getState());
            break;
        }
        case FETCH_LENDER_ADDRESS_SUCCESS: {
            const response = action.payload as LenderAddressResponse;
            if (!response) break;

            const { lenderId } = action.meta as { lenderId: string };
            store.dispatch(cacheLender(lenderId, response));
            store.dispatch(applyLenderResponse(lenderId));
            break;
        }
    }
};
